import {
  ConversionPermissions,
  CustomersPermissions,
  ImsPermissions,
  MarketingPermissions,
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
  SupportPermissions,
} from "@redotech/account-common/permissions";
import { Redirect } from "@redotech/react-router-util/redirect";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { approvalRoutes } from "@redotech/redo-merchant-general-app/approvals/route";
import { oauthRoutes } from "@redotech/redo-merchant-login-app/oauth/route";
import {
  loginRoutes,
  signupRoutes,
} from "@redotech/redo-merchant-login-app/route";
import { Navigate } from "react-router-dom";
import { amazonRoutes } from "./amazon/route";
import { askRedoRoutes } from "./app/ask-redo/routes/ask-redo-routes";
import { aeoRoutes } from "./app/navigation/routing/aeo-routes";
import { conversionRoutes } from "./app/navigation/routing/conversion-routes";
import { customersRoutes } from "./app/navigation/routing/customers-routes";
import { developerRoutes } from "./app/navigation/routing/developer-routes";
import {
  backwardsCompatibleHomeRoutes,
  homeRoutes,
} from "./app/navigation/routing/home-routes";
import { inventoryRoutes } from "./app/navigation/routing/inventory-routes";
import { legacyFinanceRedirectRoutes } from "./app/navigation/routing/legacy-finance-redirects";
import {
  backwardsCompatibleMarketingRoutes,
  marketingRoutes,
} from "./app/navigation/routing/marketing-routes";
import {
  backwardsCompatibleOrderManagementRoutes,
  orderManagementRoutes,
} from "./app/navigation/routing/order-management-routes";
import { paymentsRoutes } from "./app/navigation/routing/payments-routes";
import { redoSuppliesRoutes } from "./app/navigation/routing/redo-supplies-routes";
import { returnsRoutes } from "./app/navigation/routing/returns-routes";
import { settingRoutes } from "./app/navigation/routing/settings-routes";
import { shippingRoutes } from "./app/navigation/routing/shipping-routes";
import { shopperAgentRoutes } from "./app/navigation/routing/shopper-agent-routes";
import {
  backwardsCompatibleSupportRoutes,
  supportRoutes,
} from "./app/navigation/routing/support-routes";
import { supportV2Routes } from "./app/navigation/routing/support-v2-routes";
import { Page } from "./app/page";
import { bigcommerceRoutes } from "./bigcommerce/route";
import {
  marketingCommentSoldRoutes,
  omsCommentSoldRoutes,
} from "./commentsold/route";
import { faireRoutes } from "./faire/route";
import { gorgiasRoutes } from "./gorgias/route";
import { integrationsRoutes } from "./integrations/route";
import { OnboardingRedirect } from "./onboarding/redirect";
import { paypalRoutes } from "./paypal/route";
import { MerchantApp404Page } from "./platform/merchant-app-404-page";
import { riseRoutes } from "./rise.ai/route";
import { rithumRoutes } from "./rithum/route";
import { RouterRoot } from "./root";
import { shopifyRoutes } from "./shopify/route";
import "./styles.css";
import { temuRoutes } from "./temu/route";
import { tiktokRoutes } from "./tiktok/route";
import { uspsRoutes } from "./usps/route";
import { walmartRoutes } from "./walmart/route";
import { welcomeRoutes } from "./welcome/route";

const children: PermissionedRouteObject[] = [
  { index: true, element: <Navigate to="home" /> },
  { path: "home", children: homeRoutes },
  ...backwardsCompatibleHomeRoutes,
  ...askRedoRoutes,
  { path: "aeo", children: aeoRoutes },
  { path: "developer", children: developerRoutes },
  {
    path: "support",
    children: supportRoutes,
    handle: { permission: SupportPermissions.VIEW_SUPPORT },
  },
  {
    path: "support-v2",
    children: supportV2Routes,
    handle: { permission: SupportPermissions.VIEW_SUPPORT },
  },
  { path: "commerce-agent", children: shopperAgentRoutes },
  {
    path: "redo-concierge",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect
                match={/redo-concierge/}
                replaceValue="commerce-agent"
              />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect
                match={/redo-concierge/}
                replaceValue="commerce-agent"
              />
            ),
          },
        ],
      },
    ],
  },
  ...backwardsCompatibleSupportRoutes,
  {
    path: "marketing",
    children: marketingRoutes,
    handle: { permission: MarketingPermissions.VIEW_MARKETING },
  },
  ...backwardsCompatibleMarketingRoutes,

  {
    path: "conversion",
    children: conversionRoutes,
    handle: { permission: ConversionPermissions.ACCESS_CONVERSION },
  },

  {
    path: "shipping",
    children: shippingRoutes,
    handle: {
      permission: [
        OrderManagementPermissions.VIEW_ORDER,
        OrderManagementPermissions.SCAN_AND_VERIFY,
        OrderManagementPermissions.MANAGE_SHIPMENTS,
        OrderManagementPermissions.MANAGE_PICK_SESSIONS,
      ],
    },
  },
  {
    path: "returns",
    children: returnsRoutes,
    handle: {
      permission: [
        ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
        ReturnsAndClaimsPermissions.VIEW_RETURN,
        ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
        ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
        ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS,
        ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS,
      ],
    },
  },
  {
    path: "order-management",
    children: orderManagementRoutes,
    handle: {
      permission: [
        ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
        ReturnsAndClaimsPermissions.VIEW_RETURN,
        OrderManagementPermissions.VIEW_ORDER,
        OrderManagementPermissions.SCAN_AND_VERIFY,
        OrderManagementPermissions.MANAGE_SHIPMENTS,
        ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
        ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
        OrderManagementPermissions.MANAGE_PICK_SESSIONS,
      ],
    },
  },
  {
    path: "customer",
    children: customersRoutes,
    handle: { permission: CustomersPermissions.VIEW_CUSTOMER_RECORD },
  },
  { path: "payments", children: paymentsRoutes },
  ...legacyFinanceRedirectRoutes,
  { path: "redo-supplies", children: redoSuppliesRoutes },
  ...backwardsCompatibleOrderManagementRoutes,

  {
    path: "inventory",
    children: inventoryRoutes,
    handle: { permission: Object.values(ImsPermissions) },
  },
  {
    path: "order-management/approvals",
    children: approvalRoutes,
    handle: { permission: [ReturnsAndClaimsPermissions.VIEW_RETURN] },
  },
  { path: "settings", children: settingRoutes },
  { path: "integrations", children: integrationsRoutes },

  { path: "onboarding-redirect", element: <OnboardingRedirect /> },
  {
    path: "intent",
    async lazy() {
      const { IntentLandingPage } =
        await import("./conversion-ad/ad-landing-page");
      return { Component: IntentLandingPage };
    },
  },
  // Marketing ad URL (app.getredo.com/ppu-intent) — swap the path string
  // alone if campaigns need a different slug.
  {
    path: "ppu-intent",
    async lazy() {
      const { PpuIntentLandingPage } =
        await import("./conversion-ad/ad-landing-page");
      return { Component: PpuIntentLandingPage };
    },
  },
  { path: "login", children: loginRoutes },
  { path: "signup", children: signupRoutes },
  {
    path: "verify-identity",
    element: (
      <Page
        hideHeader={() => true}
        hideNavbar={() => true}
        hideSearchBarAndBanner={() => true}
      />
    ),
    children: [
      {
        index: true,
        async lazy() {
          const { VerifyIdentityPage } =
            await import("./verify-identity/verify-identity-page");
          return { Component: VerifyIdentityPage };
        },
      },
    ],
  },
  { path: "welcome", children: welcomeRoutes },
  { path: "oauth", children: oauthRoutes },
  {
    path: "sms-terms",
    lazy: async () => {
      const { SmsTermsPage } = await import("./sms-program-policy-page");
      return { Component: SmsTermsPage };
    },
  },
  {
    path: "sms-privacy",
    lazy: async () => {
      const { SmsPrivacyPage } = await import("./sms-program-policy-page");
      return { Component: SmsPrivacyPage };
    },
  },
  { path: "shopify", children: shopifyRoutes },
  { path: "bigcommerce", children: bigcommerceRoutes },
  {
    path: "gorgias/*",
    element: <Redirect match={/gorgias/} replaceValue="support/gorgias" />,
  },
  { path: "gorgias", children: gorgiasRoutes },
  { path: "rise-ai", children: riseRoutes },
  { path: "tiktok", children: tiktokRoutes },
  { path: "rithum", children: rithumRoutes },
  { path: "temu", children: temuRoutes },
  { path: "walmart", children: walmartRoutes },
  { path: "amazon", children: amazonRoutes },
  { path: "oms-commentsold", children: omsCommentSoldRoutes },
  { path: "marketing-commentsold", children: marketingCommentSoldRoutes },
  { path: "usps", children: uspsRoutes },
  { path: "faire", children: faireRoutes },
  { path: "paypal", children: paypalRoutes },
];

export const routes: PermissionedRouteObject[] = [
  {
    path: "/stores/:teamId/",
    Component: RouterRoot,
    children,
    errorElement: <MerchantApp404Page />,
  },
  {
    path: "/",
    Component: RouterRoot,
    errorElement: <MerchantApp404Page />,
    children,
  },
];
